import type { Tag } from '@/types';
import { IconButton } from '@astryxdesign/core/IconButton';
import { NumberInput } from '@astryxdesign/core/NumberInput';
import { Token } from '@astryxdesign/core/Token';
import { X } from 'lucide-react';
import type { DraftActivity } from '../lib/draft';
import { TagPicker, tagTokenColor } from './tag-picker';

interface ActivityRowProps {
  activity: DraftActivity;
  tags: Tag[];
  readOnly: boolean;
  onChange: (patch: Partial<DraftActivity>) => void;
  onRemove: () => void;
  onCreateTag: (label: string) => void;
}

export default function ActivityRow({
  activity,
  tags,
  readOnly,
  onChange,
  onRemove,
  onCreateTag,
}: ActivityRowProps) {
  const hours = Math.floor(activity.minutes / 60);
  const minutes = activity.minutes % 60;
  const appliedTags = activity.tags
    .map((tagId) => tags.find((tag) => tag.id === tagId))
    .filter((tag) => tag != null);

  return (
    <div className="iorio-reports-activity">
      <div className="flex items-center gap-2">
        <input
          value={activity.text}
          onChange={(event) => onChange({ text: event.target.value })}
          disabled={readOnly}
          placeholder="Describe una actividad…"
          className="iorio-reports-field"
        />
        <div className="iorio-reports-duration">
          <NumberInput
            label="Horas"
            isLabelHidden
            value={hours}
            onChange={(value) => onChange({ minutes: value * 60 + minutes })}
            min={0}
            max={24}
            isIntegerOnly
            isDisabled={readOnly}
            units="h"
            size="sm"
            hasClear={false}
          />
          <NumberInput
            label="Minutos"
            isLabelHidden
            value={minutes}
            onChange={(value) => onChange({ minutes: hours * 60 + value })}
            min={0}
            max={59}
            isIntegerOnly
            isDisabled={readOnly}
            units="m"
            size="sm"
            hasClear={false}
          />
        </div>
        {!readOnly && (
          <IconButton
            label="Quitar actividad"
            tooltip="Quitar actividad"
            icon={<X size={14} />}
            variant="ghost"
            size="sm"
            onClick={onRemove}
          />
        )}
      </div>
      {(appliedTags.length > 0 || !readOnly) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {appliedTags.map((tag) => (
            <Token
              key={tag.id}
              label={tag.label}
              color={tagTokenColor(tag)}
              size="sm"
              onRemove={
                readOnly
                  ? undefined
                  : () => onChange({ tags: activity.tags.filter((tagId) => tagId !== tag.id) })
              }
            />
          ))}
          {!readOnly && (
            <TagPicker
              tags={tags}
              appliedTagIds={activity.tags}
              onAdd={(tagId) => onChange({ tags: [...activity.tags, tagId] })}
              onCreate={onCreateTag}
            />
          )}
        </div>
      )}
    </div>
  );
}
